/* eslint-disable camelcase */
import React, { useEffect, ReactChild } from "react";
import { useInView, IntersectionOptions } from "react-intersection-observer";
import { JobSearchHit } from "@themuse/design-system";
import { triggerSnowplowImpressionEvent } from "@themuse/snowplow-js-client";
import { formatLog } from "utils/logging";
import { isBrowser } from "utils/browser";

interface IOskar {
    setGlobalUserProperties: (properties: any) => void;
    setGlobalEventProperties: (properties: any) => void;
    capture: (eventType: string, eventProperties: any) => void;
    setEnvironment: (environment: string, url?: string) => void;
    getOskarID: (callback: (oskarID: string) => void) => void;
    getSessionID: (callback: (sessionID: string) => void) => void;
}

interface OskarWindow extends Window {
    OskarTS: IOskar;
}

interface TrackedElementProps {
    children: ReactChild;
    eventName: string;
    eventProps?: Object;
    eventType?: string;
    enabled?: boolean;
    callback?: () => void;
}

interface BaseProps {
    app_version_id: string;
    page_referrer: string;
    query_string: string;
    path: string;
    experiments: any[];
}

interface EventProps {
    sp_impression_component?: string;
    experiment_arms?: any[];
    experiments?: any[];
    link_href?: string;
    link_name?: string;
    page_referrer?: string;
    path?: string;
    placement_position?: string;
    query_string?: string;
    right_click?: boolean;
    status_code?: number;
    search_origin?: string;
    search_term?: string;
    search_location?: string;
    search_company?: string;
    search_company_short_name?: string;
    source?: string;
    name?: string;
    model_type?: string;
    filter_source?: string;
    value?: string;
}

declare const window: OskarWindow;

const appVersion = process.env.VERSION?.split(".").slice(0, 2).join(".");

const hasOskar = () => typeof window.OskarTS !== "undefined";

/**
 * Generate the base properties for any event. We added url data, experiment
 * data. And a few other things as well.
 *
 * @export
 * @returns {BaseProps}
 */
const genBaseProps = (): BaseProps => ({
    app_version_id: appVersion,
    page_referrer: document?.referrer ?? "",
    query_string: window?.document?.location?.search ?? "",
    path: window?.document?.location?.pathname ?? "",
    experiments: []
});

/**
 * Tracks an event name and event properties. It add some base
 * properties including page data and referrer data to the event being tracked
 *
 * @param {string} eventName
 * @param {*} [eventProps={}]
 */
const museTrack = (eventName: string, eventProps: EventProps) => {
    try {
        if (isBrowser && hasOskar) {
            const baseProps = genBaseProps();
            window?.OskarTS?.capture(eventName, { ...eventProps, ...baseProps });
        }
    } catch (error) {
        const msg = "OskarTS museTrack error";
        console.error(msg, JSON.stringify(formatLog({ error })));
    }
};

/**
 * Tracks a user
 *
 * @param {*} [userProps={}]
 */
const userTrack = (userProps: Object = {}) => {
    try {
        if (isBrowser && hasOskar) {
            window?.OskarTS?.setGlobalUserProperties({ ...userProps });
        }
    } catch (error) {
        const msg = "OskarTS userTrack error";
        console.error(msg, JSON.stringify(formatLog({ error })));
    }
};

/**
 * TrackedElement: Wraps an element in an intersection observer with tracking props.
 */
const TrackedElement = (props: TrackedElementProps & IntersectionOptions) => {
    const {
        children, eventName, eventProps, enabled = true, callback, ...restProps
    } = props;

    const intersectionProps: IntersectionOptions = {
        triggerOnce: true,
        rootMargin: "0px",
        ...restProps
    };
    const { ref, inView, entry } = useInView(intersectionProps);
    let viewed = false;

    const trackingProps: any = { ...eventProps };

    useEffect(() => {
        if (inView && enabled) {
            museTrack(eventName, eventProps);
            if (callback) {
                callback();
            }
            if (trackingProps?.sp_impression_component && !viewed && inView) {
                triggerSnowplowImpressionEvent(entry.target as HTMLElement);
                viewed = true;
            }
        }
    }, [inView]);

    return (
        <div
            ref={ref}
            data-tracking-props={JSON.stringify(eventProps)}
        >
            {children}
        </div>
    );
};

/**
 * genJobTrackingProps: Generate the props for a tracked element.
 */
const genJobTrackingProps = (job: JobSearchHit) => ({
    company_id: job.hit.company.legacy_id,
    company_name: job.hit.company.name,
    company_short_name: job.hit.company.short_name,
    job_id: job.hit.legacy_id,
    job_level: job.hit.levels.map((level) => level.code),
    job_locations: job.hit.locations.map((location) => location.address),
    job_name: job.hit.title,
    job_short_name: job.hit.short_title,
    job_type: job.hit.apply_type,
    sp_impression_component: "tile",
    sp_impression_tile_type: "job",
    sp_tile_type: "job",
    sp_tile_entity_id: job.hit.legacy_id.toString(),
    sp_tile_entity_uuid: job.hit.id,
    sp_tile_company_id: job.hit.company.legacy_id.toString(),
    sp_tile_location: "search",
});

/**
 * Find a specific type of ancestor of this element.
 */
const findParentElement = (el: Element, elementTypes: string[]): Element | null => {
    if (elementTypes.includes(el.tagName)) {
        return el;
    }
    if (el.parentElement && el.parentElement !== document.body) {
        return findParentElement(el.parentElement, elementTypes);
    }
    return null;
};

/**
 * Fire an event only if the element or one of its parents is an "A".
 */
const getLinkTrackingProps = (e: MouseEvent | React.MouseEvent<HTMLElement, HTMLAnchorElement>) => {
    const el: Element | HTMLAnchorElement | null = findParentElement(e.target as Element, ["A"]);
    if (!el) {
        return;
    }
    const href = (el as HTMLAnchorElement).getAttribute("href");
    const trackingProps = {
        experiment_arms: [],
        experiments: [],
        link_href: href,
        link_name: el.textContent,
        page_referrer: document.referrer,
        path: window.location.pathname,
        placement_position: "",
        query_string: window.location.search,
        right_click: e.type === "contextmenu",
        status_code: 200,
    };
    museTrack("Global Link Clicked", trackingProps);
};

export {
    TrackedElement,
    appVersion,
    findParentElement,
    genBaseProps,
    genJobTrackingProps,
    getLinkTrackingProps,
    hasOskar,
    museTrack,
    userTrack
};

export type {
    BaseProps,
    IOskar,
    OskarWindow,
    TrackedElementProps
};
